import { INTENT_TYPE } from '../../constants';
import { type IntentMatcher } from '../../types';

/**
 * PORTED FROM: `HeuristicUtils.isSingleWord(String)`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/util/HeuristicUtils.java#lines-354
 *
 * This is the single-argument, space-only overload — the one
 * It is a literal "contains no space" test.
 *
 * NOT PORTED: the two-argument `isSingleWord(String, boolean)` overload, which
 * adds locale-aware CJK/Thai word segmentation behind the
 * `ENABLE_MULTILINGUAL_FPS_CLASSIFIER` gate. That variant is only reachable
 * from the `SINGLE_WORD` GNIN branch, which this iteration does not port. It
 * needs real segmentation (`Intl.Segmenter`) rather than a regex, so it should
 * land with that matcher rather than being half-built here.
 *
 * Consequence worth knowing: because this checks for the ASCII space only,
 * unspaced CJK input counts as a single word — same as upstream with the gate
 * off.
 */
export const isSingleWord = (query: string): boolean => !query.trim().includes(' ');

/**
 * PORTED FROM: the fourth branch of `GninHeuristicStrategy.classifyInternal`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/services/strategy/GninHeuristicStrategy.java#lines-92
 *
 * ```java
 * if (HeuristicUtils.isSingleWord(trimmedQuery, multilingualEnabled)) {
 *     return new JiraClassificationResult(GNIN, SINGLE_WORD);
 * }
 * ```
 *
 * A lone word is treated as navigational, on the assumption the user is naming
 * a thing rather than describing one.
 *
 * Upstream passes a `multilingualEnabled` flag that switches to grapheme
 * segmentation, so CJK text without spaces can still count as multiple words.
 * That variant is not ported, so this always behaves as the gate-disabled path.
 */
export const singleWordMatcher: IntentMatcher = {
	pattern: 'SINGLE_WORD',
	type: INTENT_TYPE.GNIN,
	matches: isSingleWord,
};
